/**
 * Centralized Branding Configuration
 * Fetches from API and caches locally
 *
 * Two layers: the system branding (every visitor) and the signed-in user's
 * tenant branding on top. They are stored apart so that re-applying the
 * system branding on each page load never wipes out the tenant's choices.
 */

export interface BrandingConfig {
  logoUrl: string;
  logoAlt: string;
  appName: string;
  tagline: string;
  faviconUrl?: string;
  fontFamily?: string;
  primaryColor?: string;
}

/** The tenant's own choices, as the API returns them. */
export interface TenantBrandingLayer {
  logo_url?: string | null;
  favicon_url?: string | null;
  font_family?: string | null;
  primary_color?: string | null;
}

export interface SystemBranding {
  app_name: string;
  logo_url: string | null;
  favicon_url: string | null;
  tagline: string;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  default_theme: string;
  font_family?: string | null;
}

// Default branding configuration
export const defaultBranding: BrandingConfig = {
  logoUrl: '/neimus-logo.png',
  logoAlt: 'NEIMUS360',
  appName: 'NEIMUS360',
  tagline: 'Environmental Intelligence Platform',
  faviconUrl: '/logo-icon.png',
  fontFamily: '',
};

const DEFAULT_FONT_STACK = "'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
const FONT_STACKS: Record<string, string> = {
  Inter: DEFAULT_FONT_STACK,
  Roboto: "'Roboto', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
  'Open Sans': "'Open Sans', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
  Lato: "'Lato', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
  Poppins: "'Poppins', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
};

function getFontStack(fontFamily?: string): string {
  if (!fontFamily) return DEFAULT_FONT_STACK;
  return FONT_STACKS[fontFamily] || `${JSON.stringify(fontFamily)}, system-ui, -apple-system, BlinkMacSystemFont, sans-serif`;
}

// How far each shade moves from the chosen colour: towards white for the
// light shades, towards black for the dark ones. 500 is the colour itself.
const PRIMARY_SHADE_MIX: Record<string, number> = {
  '50': 0.95, '100': 0.9, '200': 0.75, '300': 0.55, '400': 0.3, '500': 0,
  '600': -0.15, '700': -0.3, '800': -0.45, '900': -0.55, '950': -0.7,
};
const HEX_COLOUR = /^#[0-9a-f]{6}$/i;

function validColour(value?: string | null): string | undefined {
  const colour = value?.trim();
  return colour && HEX_COLOUR.test(colour) ? colour.toLowerCase() : undefined;
}

/** The primary palette (50-950) built around one colour. */
export function primaryPalette(hex: string): Record<string, string> {
  const base = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return Object.fromEntries(Object.entries(PRIMARY_SHADE_MIX).map(([shade, mix]) => {
    const target = mix > 0 ? 255 : 0;
    const rgb = base.map((c) => Math.round(c + (target - c) * Math.abs(mix)));
    return [shade, `#${rgb.map((c) => c.toString(16).padStart(2, '0')).join('')}`];
  }));
}

export function applyBranding(config: BrandingConfig): void {
  const style = document.documentElement.style;
  style.setProperty('--font-sans', getFontStack(config.fontFamily));
  document.documentElement.dataset.brandFont = config.fontFamily || 'system';
  // Every primary-* colour in the interface reads these; without an
  // override the built-in blue from index.css applies.
  const colour = validColour(config.primaryColor);
  const palette = colour ? primaryPalette(colour) : null;
  for (const shade of Object.keys(PRIMARY_SHADE_MIX)) {
    if (palette) style.setProperty(`--color-primary-${shade}`, palette[shade]);
    else style.removeProperty(`--color-primary-${shade}`);
  }
}

// Local storage keys: the system layer, its API cache and the tenant layer
const BRANDING_STORAGE_KEY = 'neimus360_branding';
const BRANDING_CACHE_KEY = 'neimus360_branding_cache';
const BRANDING_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const TENANT_BRANDING_KEY = 'neimus360_tenant_branding';

function getSystemBranding(): BrandingConfig {
  try {
    const stored = localStorage.getItem(BRANDING_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return { ...defaultBranding, ...parsed };
    }
  } catch (e) {
    console.warn('Failed to load custom branding:', e);
  }
  return defaultBranding;
}

function getTenantLayer(): TenantBrandingLayer | null {
  try {
    const stored = localStorage.getItem(TENANT_BRANDING_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

/**
 * Get current branding configuration: the system branding with the
 * signed-in tenant's choices on top. Empty tenant fields keep the system's.
 */
export function getBranding(): BrandingConfig {
  const system = getSystemBranding();
  const tenant = getTenantLayer();
  if (!tenant) return system;
  const chosen = (value?: string | null) => value?.trim() || undefined;
  return {
    ...system,
    logoUrl: chosen(tenant.logo_url) ?? system.logoUrl,
    faviconUrl: chosen(tenant.favicon_url) ?? system.faviconUrl,
    fontFamily: chosen(tenant.font_family) ?? system.fontFamily,
    primaryColor: validColour(tenant.primary_color) ?? system.primaryColor,
  };
}

/** Apply whatever is current to the page and tell the logo components. */
function refreshBranding(): void {
  const current = getBranding();
  applyBranding(current);
  if (current.appName) {
    document.title = current.appName;
  }
  if (current.faviconUrl) {
    updateFavicon(current.faviconUrl);
  }
  window.dispatchEvent(new CustomEvent('branding-updated', { detail: current }));
}

/**
 * Show the signed-in user's tenant branding over the system branding, or
 * pass null to go back to the system branding (e.g. on signing out).
 */
export function applyTenantBranding(layer: TenantBrandingLayer | null): void {
  try {
    if (layer) {
      const { logo_url, favicon_url, font_family, primary_color } = layer;
      localStorage.setItem(TENANT_BRANDING_KEY, JSON.stringify({ logo_url, favicon_url, font_family, primary_color }));
    } else {
      localStorage.removeItem(TENANT_BRANDING_KEY);
    }
  } catch (e) {
    console.warn('Failed to store tenant branding:', e);
  }
  refreshBranding();
}

/**
 * Save the system branding (the tenant layer stays on top of it)
 */
export function saveBranding(config: Partial<BrandingConfig>): void {
  try {
    const current = getSystemBranding();
    const definedConfig = Object.fromEntries(
      Object.entries(config).filter(([, value]) => value !== undefined)
    ) as Partial<BrandingConfig>;
    const updated = { ...current, ...definedConfig };
    localStorage.setItem(BRANDING_STORAGE_KEY, JSON.stringify(updated));
    refreshBranding();
  } catch (e) {
    console.error('Failed to save branding:', e);
  }
}

/**
 * Update the favicon dynamically
 */
function updateFavicon(url: string): void {
  // Normalize URL
  let faviconUrl = url.startsWith('http') || url.startsWith('/')
    ? url
    : `/api/v1${url}`;

  // Add cache-busting parameter
  const cacheBuster = `?v=${Date.now()}`;
  faviconUrl = faviconUrl + cacheBuster;

  // Determine file type for proper MIME type
  const isSvg = url.toLowerCase().includes('.svg');
  const isPng = url.toLowerCase().includes('.png');
  const isIco = url.toLowerCase().includes('.ico');

  const mimeType = isSvg ? 'image/svg+xml' : isPng ? 'image/png' : isIco ? 'image/x-icon' : 'image/png';

  // Remove all existing favicon links first
  const existingIcons = document.querySelectorAll("link[rel='icon'], link[rel='shortcut icon']");
  existingIcons.forEach(icon => icon.remove());

  // Create new favicon link with proper type
  const iconLink = document.createElement('link');
  iconLink.rel = 'icon';
  iconLink.type = mimeType;
  iconLink.href = faviconUrl;
  document.head.appendChild(iconLink);

  // Also add shortcut icon for older browsers
  const shortcutIcon = document.createElement('link');
  shortcutIcon.rel = 'shortcut icon';
  shortcutIcon.type = mimeType;
  shortcutIcon.href = faviconUrl;
  document.head.appendChild(shortcutIcon);

  // Update apple-touch-icon
  let appleIconLink = document.querySelector("link[rel='apple-touch-icon']") as HTMLLinkElement;
  if (!appleIconLink) {
    appleIconLink = document.createElement('link');
    appleIconLink.rel = 'apple-touch-icon';
    document.head.appendChild(appleIconLink);
  }
  appleIconLink.href = faviconUrl;

  console.log('Favicon updated to:', faviconUrl);
}

/**
 * Reset branding to defaults
 */
export function resetBranding(): void {
  localStorage.removeItem(BRANDING_STORAGE_KEY);
  localStorage.removeItem(BRANDING_CACHE_KEY);
  refreshBranding();
}

/**
 * Fetch system branding from API and apply it
 */
export async function fetchAndApplyBranding(): Promise<void> {
  try {
    applyBranding(getBranding());

    // Check cache first
    const cached = localStorage.getItem(BRANDING_CACHE_KEY);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < BRANDING_CACHE_TTL) {
        applyBrandingFromApi(data);
        return;
      }
    }

    // Fetch from API
    const response = await fetch('/api/v1/settings/branding/system');
    if (response.ok) {
      const data: SystemBranding = await response.json();

      // Cache the result
      localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify({
        data,
        timestamp: Date.now(),
      }));

      applyBrandingFromApi(data);
    }
  } catch (e) {
    console.warn('Failed to fetch system branding:', e);
    // Fall back to localStorage or defaults
    const branding = getBranding();
    applyBranding(branding);
    if (branding.appName) {
      document.title = branding.appName;
    }
  }
}

/**
 * Apply branding from API response
 */
function applyBrandingFromApi(data: SystemBranding): void {
  // Update document title
  if (data.app_name) {
    document.title = data.app_name;
  }

  // Update favicon
  if (data.favicon_url) {
    updateFavicon(data.favicon_url);
  }

  // Save to local storage for components to use
  const brandingUpdate: Partial<BrandingConfig> = {
    appName: data.app_name || 'NEIMUS360',
    logoUrl: data.logo_url || '/neimus-logo.png',
    tagline: data.tagline || 'Environmental Intelligence Platform',
    faviconUrl: data.favicon_url || undefined,
  };

  if (data.font_family !== undefined) {
    brandingUpdate.fontFamily = data.font_family || '';
  }

  saveBranding(brandingUpdate);
}
